import { object, string, number, array, node, bool } from 'prop-types'
import { useMedia } from 'react-use'
import { components, utils } from '@digital-commerce/shared-utils'
import { not, isNil, isEmpty } from 'ramda'

import {
  Typography,
  Box,
  FlexGrid,
  Link,
  Image,
  Notification,
  List,
  Spacer
} from '@telus-uds/components-web'
import Icons from '@telus-uds/palette-allium/build/web/icons'

import PriceInfo from './PriceInfo'
import { getCartChangeURL, getCheckoutDescription, getImageUrlAndAltText } from './utils'

import { LogoContainer, ProductColumnContainer } from './styles'

import AppliedPromoCode from './AppliedPromoCode'
import { isIMEIDuplicated, isCartValid, isItemValid } from '../../../utils/cart'
import useTranslate from '../../../hooks/useTranslate'
import useCart from '../../../hooks/useCart'
import FormattedTrademarkTextWrapper from '../../atoms/FormattedTrademarkTextWrapper'
import ShippingSla from '../RealTimeInventory/ShippingSla'

const { ShouldRender } = components
const { getDisplayName } = utils.cart

const BasicInfo = ({
  cartItem, // passed in by CartItem.jsx
  subHeading,
  firstBillSubTotal,
  monthlySubTotal,
  dueTodaySubTotal,
  hidePricing,
  changeUrl,
  term,
  appendAttributes,
  attributesOverride,
  appliedPromoCodeText,
  termsAndConditions,
  itemImageOverride,
  itemDisplayNameOverride,
  mobilityBannerData,
  financedAccessoryBalanceMessage,
  inventory,
  mobOutOfStockNotification
}) => {
  const { cart } = useCart()
  const translate = useTranslate()

  // Relying on FlexFlexGrid to hide components still renders them in the DOM causing duplicates and unwanted spaces
  const isMobile = useMedia('(max-width: 767px)')

  const displayName = itemDisplayNameOverride ?? getDisplayName(cartItem)

  const { imageUrl, imageAlt } = getImageUrlAndAltText(itemImageOverride ?? cartItem)

  const appliedPromoCode = appliedPromoCodeText ? (
    <AppliedPromoCode promoCode={appliedPromoCodeText} />
  ) : null

  const termText = term ? (
    <Typography variant={{ size: 'small' }} testID="service-term">
      {term}
    </Typography>
  ) : null

  const changeLink = () => {
    if (!changeUrl) return null

    const changeUrlLink = getCartChangeURL(changeUrl, displayName)

    return (
      <Typography variant={{ size: 'small' }} align="center">
        <Link
          href={changeUrlLink}
          testID="change-link"
          icon={Icons.Edit}
          iconPosition="left"
          variant={{ style: 'inline', size: 'small' }}
        >
          {translate('changeLinkText')}
        </Link>
      </Typography>
    )
  }

  const renderDetails = (service) => {
    const attributes = attributesOverride || getCheckoutDescription(service)

    const attributesMap = (label) => (
      <List.Item key={label} testID={label}>
        <FormattedTrademarkTextWrapper text={label} />
      </List.Item>
    )

    return [...attributes, ...appendAttributes].map(attributesMap)
  }

  const bannerNotification = (bannerData) => {
    return bannerData ? (
      <Box bottom={4}>
        <Notification
          tokens={{ icon: '' }}
          testID={`${bannerData.type}-notification`}
          dataSet={{ 'di-mask': true }}
        >
          <Typography variant={{ size: 'medium', bold: true }}>
            {translate(`${bannerData.translationKey}`)}
          </Typography>
          <Typography variant={{ size: 'medium' }} data-di-mask>
            {bannerData.mobInfo}
          </Typography>
        </Notification>
      </Box>
    ) : null
  }

  const errorMessage = (isIMEIDuplicate) =>
    isIMEIDuplicate ? (
      <Typography variant={{ size: 'medium' }} testID="item-imei-error-message">
        {translate('orbit.duplicatedIMEIItem')}
      </Typography>
    ) : (
      <Typography variant={{ size: 'medium' }} testID="item-error-message">
        {translate('productInvalid')}
      </Typography>
    )

  const itemValidNotification = isItemValid(cart, cartItem) ? null : (
    <Box bottom={4}>
      <Notification
        variant={{ validation: 'error' }}
        testID={`${cartItem.id}-product-unavailable-notification`}
      >
        {errorMessage(isIMEIDuplicated(cart))}
      </Notification>
    </Box>
  )

  return (
    <>
      <FlexGrid.Row>
        <FlexGrid.Col>
          <Box>
            {mobOutOfStockNotification}
            {bannerNotification(mobilityBannerData)}
            {itemValidNotification}
          </Box>
        </FlexGrid.Col>
      </FlexGrid.Row>
      <FlexGrid.Row>
        <FlexGrid.Col xs={12} md={2}>
          <ShouldRender when={not(isEmpty(imageUrl))}>
            {() => (
              <LogoContainer>
                <Image width={120} height={120} src={imageUrl} alt={imageAlt} />
              </LogoContainer>
            )}
          </ShouldRender>
          <FlexGrid.Row horizontalAlign="center">
            <ShouldRender when={!isMobile}>{changeLink()}</ShouldRender>
          </FlexGrid.Row>
        </FlexGrid.Col>
        <FlexGrid.Col xs={12} md={10}>
          <ProductColumnContainer>
            <FlexGrid gutter={false}>
              <FlexGrid.Row>
                <FlexGrid.Col xs={12} sm={12} md={hidePricing ? 12 : 7}>
                  <Box>
                    <Typography variant={{ size: 'small' }} testID="item-category">
                      {subHeading}
                    </Typography>
                    <Spacer space={1} />
                    <Typography variant={{ size: 'h2' }} testID="BasicInfo-displayName">
                      {displayName}
                      {isCartValid(cart) && termsAndConditions && termsAndConditions}
                    </Typography>
                    <Spacer space={1} />
                    {termText}
                    <Spacer space={1} />
                    <ShouldRender when={!isMobile && not(isNil(inventory))}>
                      {() => (
                        <FlexGrid.Row>
                          <FlexGrid.Col>
                            <ShippingSla inventory={inventory} />
                          </FlexGrid.Col>
                        </FlexGrid.Row>
                      )}
                    </ShouldRender>
                    <ShouldRender when={isMobile}>
                      <ShouldRender when={not(isNil(inventory))}>
                        {() => (
                          <FlexGrid.Row>
                            <FlexGrid.Col>
                              <ShippingSla inventory={inventory} />
                              <Spacer space={1} />
                            </FlexGrid.Col>
                          </FlexGrid.Row>
                        )}
                      </ShouldRender>
                      <FlexGrid.Row>
                        <FlexGrid.Col>{changeLink()}</FlexGrid.Col>
                      </FlexGrid.Row>
                    </ShouldRender>
                  </Box>
                </FlexGrid.Col>
                <ShouldRender when={!hidePricing}>
                  <FlexGrid.Col xs={0} md={5} justifyContent="right">
                    <PriceInfo
                      oneTimeSubTotal={dueTodaySubTotal}
                      firstBillSubTotal={firstBillSubTotal}
                      monthlySubTotal={monthlySubTotal}
                      mobileViewPoints={false}
                    />
                  </FlexGrid.Col>
                </ShouldRender>
              </FlexGrid.Row>
              <FlexGrid.Row>
                <FlexGrid.Col xs={12}>
                  <Box>
                    {appliedPromoCode}
                    {financedAccessoryBalanceMessage}
                  </Box>
                  <Box vertical={4}>
                    <List variant={{ listStyle: 'circle' }} testID="cart-item-attrs">
                      {renderDetails(cartItem)}
                    </List>
                  </Box>
                </FlexGrid.Col>
              </FlexGrid.Row>
              <ShouldRender when={isMobile && !hidePricing}>
                {() => (
                  <Box bottom={2}>
                    <PriceInfo
                      oneTimeSubTotal={dueTodaySubTotal}
                      firstBillSubTotal={firstBillSubTotal}
                      monthlySubTotal={monthlySubTotal}
                      mobileViewPoints={true}
                    />
                  </Box>
                )}
              </ShouldRender>
            </FlexGrid>
          </ProductColumnContainer>
        </FlexGrid.Col>
      </FlexGrid.Row>
    </>
  )
}

BasicInfo.propTypes = {
  cartItem: object,
  subHeading: string.isRequired,
  firstBillSubTotal: number,
  monthlySubTotal: number,
  dueTodaySubTotal: number,
  hidePricing: bool,
  changeUrl: string,
  term: string,
  appendAttributes: array,
  attributesOverride: array,
  appliedPromoCodeText: string,
  termsAndConditions: node,
  itemImageOverride: object,
  itemDisplayNameOverride: string,
  mobilityBannerData: object,
  financedAccessoryBalanceMessage: node,
  inventory: object,
  mobOutOfStockNotification: node
}
BasicInfo.defaultProps = {
  cartItem: {},
  dueTodaySubTotal: undefined,
  hidePricing: false,
  changeUrl: undefined,
  firstBillSubTotal: undefined,
  monthlySubTotal: 0,
  term: null,
  appendAttributes: [],
  attributesOverride: null,
  appliedPromoCodeText: null,
  termsAndConditions: undefined,
  itemImageOverride: undefined,
  itemDisplayNameOverride: undefined,
  mobilityBannerData: undefined,
  financedAccessoryBalanceMessage: null,
  inventory: undefined,
  mobOutOfStockNotification: null
}

export default BasicInfo
